/**
 * documentLibrary.js — aggregates every category file into the searchable library.
 *
 * Add a document by adding it to registry.js and to its category file. Nothing else needs wiring:
 * the library, search, filters, finder, bundles, roadmap and checklist all read from here.
 */

import { FORMATION_DOCS } from "./documents/formation.js";
import { CONFIDENTIALITY_DOCS } from "./documents/confidentiality.js";
import { PRODUCT_DEVELOPMENT_DOCS } from "./documents/productDevelopment.js";
import { MANUFACTURING_DOCS } from "./documents/manufacturing.js";
import { SUPPLY_DOCS } from "./documents/supply.js";
import { DISTRIBUTION_DOCS } from "./documents/distribution.js";
import { BROKERS_DOCS } from "./documents/brokers.js";
import { RETAIL_DOCS } from "./documents/retail.js";
import { LOGISTICS_DOCS } from "./documents/logistics.js";
import { PEOPLE_DOCS } from "./documents/people.js";
import { MARKETING_DOCS } from "./documents/marketing.js";
import { AFFILIATE_DOCS } from "./documents/affiliate.js";
import { LICENSING_DOCS } from "./documents/licensing.js";
import { INVESTORS_DOCS } from "./documents/investors.js";
import { FINANCING_DOCS } from "./documents/financing.js";
import { ECOMMERCE_DOCS } from "./documents/ecommerce.js";
import { EVENTS_DOCS } from "./documents/events.js";
import { INTERNATIONAL_DOCS } from "./documents/international.js";
import { DISPUTES_DOCS } from "./documents/disputes.js";
import { MA_DOCS } from "./documents/ma.js";

import { CATEGORY_BY_ID } from "./categories.js";
import { IMPORTANCE_BY_ID, STAGE_BY_ID } from "./stages.js";

export const DOCUMENTS = [
  ...FORMATION_DOCS,
  ...CONFIDENTIALITY_DOCS,
  ...PRODUCT_DEVELOPMENT_DOCS,
  ...MANUFACTURING_DOCS,
  ...SUPPLY_DOCS,
  ...DISTRIBUTION_DOCS,
  ...BROKERS_DOCS,
  ...RETAIL_DOCS,
  ...LOGISTICS_DOCS,
  ...PEOPLE_DOCS,
  ...MARKETING_DOCS,
  ...AFFILIATE_DOCS,
  ...LICENSING_DOCS,
  ...INVESTORS_DOCS,
  ...FINANCING_DOCS,
  ...ECOMMERCE_DOCS,
  ...EVENTS_DOCS,
  ...INTERNATIONAL_DOCS,
  ...DISPUTES_DOCS,
  ...MA_DOCS,
];

export const DOC_BY_ID = Object.fromEntries(DOCUMENTS.map((d) => [d.id, d]));
export const DOCUMENT_COUNT = DOCUMENTS.length;

export function docsForCategory(categoryId) {
  return DOCUMENTS.filter((d) => d.category === categoryId);
}

export function docsForStage(stageId) {
  return DOCUMENTS.filter((d) => d.stages.includes(stageId));
}

export function docsForFilter(filterLabel) {
  return DOCUMENTS.filter((d) => {
    const cat = CATEGORY_BY_ID[d.category];
    return cat && Array.isArray(cat.filters) && cat.filters.includes(filterLabel);
  });
}

export function docsWithTemplates() {
  return DOCUMENTS.filter((d) => Boolean(d.templateId));
}

/** Documents whose entries are explicitly attorney-led. */
export function attorneyLedDocs() {
  return DOCUMENTS.filter((d) => d.attorneyReview === "strongly-recommended");
}

const NORM = (s) => String(s || "").toLowerCase();

/**
 * Weighted search across name, aliases, subcategory, parties and the guidance text.
 * Deterministic: equal scores fall back to alphabetical name order.
 */
export function searchDocuments(query, { limit = 40 } = {}) {
  const q = NORM(query).trim();
  if (!q) return [];
  const terms = q.split(/\s+/).filter(Boolean);

  const scored = DOCUMENTS.map((d) => {
    const name = NORM(d.name);
    const aliases = (d.aliases || []).map(NORM);
    const haystack = [
      name,
      ...aliases,
      NORM(d.subcategory),
      NORM(d.parties),
      NORM(d.whenYouNeedIt),
      NORM(d.whatItDoes),
      NORM(d.whyItMatters),
      ...(d.majorTerms || []).map(NORM),
    ].join("  ");

    let score = 0;
    for (const t of terms) {
      if (name === t) score += 100;
      else if (name.startsWith(t)) score += 40;
      else if (name.includes(t)) score += 25;
      if (aliases.some((a) => a.includes(t))) score += 18;
      if (NORM(d.subcategory).includes(t)) score += 8;
      if (haystack.includes(t)) score += 4;
    }
    // Nudge the documents a founder is most likely to need to the top on ties.
    score -= (IMPORTANCE_BY_ID[d.importance]?.weight ?? 3) * 0.25;
    return { doc: d, score };
  })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || a.doc.name.localeCompare(b.doc.name));

  return scored.slice(0, limit).map((r) => r.doc);
}

/** Stage-ordered grouping used by the roadmap and checklist views. */
export function groupByStage() {
  const out = {};
  for (const s of Object.keys(STAGE_BY_ID)) out[s] = [];
  for (const d of DOCUMENTS) for (const s of d.stages) if (out[s]) out[s].push(d);
  return out;
}

/** The documents most founders should look at first, used for the dashboard. */
export const FEATURED_DOC_IDS = [
  "co-packer-agreement",
  "beverage-distributor-agreement",
  "mutual-nda",
  "three-pl-agreement",
  "formula-ownership-agreement",
  "cpg-broker-agreement",
  "influencer-agreement",
  "co-founder-agreement",
];
